import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';

import get from 'lodash/get';
import map from 'lodash/map';
import noop from 'lodash/noop';

import { useSnackBar } from '@sbv-clients/ui-shared';
import { UnwrapArray } from '@sbv-clients/util-misc';

import { Api, ShiftTypeCacheKeys, UpdateShiftTypeDto, useApi } from '../index';

const updateShiftType =
  (api: Api) => (body: UpdateShiftTypeDto & { shiftTypeId: number }) => {
    const { shiftTypeId, ...updateShiftTypeDto } = body;
    return api.shiftTypeApi.shiftTypeIdPut({
      id: shiftTypeId,
      updateShiftTypeDto,
    });
  };

export const useUpdateShiftType = () => {
  const api = useApi();
  const queryClient = useQueryClient();
  const { notifyError } = useSnackBar();

  return useMutation(updateShiftType(api), {
    onSuccess: async () => {
      queryClient
        .invalidateQueries([ShiftTypeCacheKeys.ShiftTypes])
        .catch(noop);
    },
    onError: async (error) => {
      const message =
        get(error, 'response.data.message', '') || get(error, 'message', '');
      notifyError(message);
    },
  });
};
export const useShiftTypesGet = () => {
  const api = useApi();
  return useQuery({
    queryKey: [ShiftTypeCacheKeys.ShiftTypes],
    queryFn: () =>
      api.shiftTypeApi
        .shiftTypeGet({})
        .then((response) => response.data.data ?? []),
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });
};
export type ShiftTypeResponseType = NonNullable<
  UnwrapArray<ReturnType<typeof useShiftTypesGet>['data']>
>;

export const useShiftTypeOptionsGet = () => {
  const { data: shiftTypes = [], isLoading, isSuccess } = useShiftTypesGet();
  const options = useMemo(
    () =>
      map(shiftTypes, (item) => ({
        id: String(item.shiftTypeId),
        label: item.shiftTypeName ?? '',
        value: item.shiftTypeId as number,
        meta: item,
      })),
    [shiftTypes]
  );
  return { options, isLoading, isSuccess };
};
